/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ensureExternalUrl } from '@/lib/utils';
import { OrderLineItem } from './order-line-item';
import { getStatusBadgeVariant, getStatusLabelKey } from './helpers';
import type { SomFulfillmentOrder } from '@/lib/order/guest-lookup.server';

export interface FulfillmentGroupCardProps {
    group: SomFulfillmentOrder;
}

/**
 * One fulfillment status group: a status badge, its line items, and (when shipped) carrier
 * tracking links. Mirrors SFRA's `order/fulfillmentCard` + `order/orderItems` templates.
 */
export function FulfillmentGroupCard({ group }: FulfillmentGroupCardProps) {
    const { t } = useTranslation('trackOrder');
    const items = group.orderItems?.orderItems ?? [];
    const tracking = group.trackingData ?? [];

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
                <CardTitle className="text-base">{t('sections.items')}</CardTitle>
                <Badge variant={getStatusBadgeVariant(group)}>{t(getStatusLabelKey(group))}</Badge>
            </CardHeader>
            <CardContent>
                <div className="divide-y divide-border">
                    {items.map((item, index) => (
                        <OrderLineItem
                            key={item.orderItemSummaryId ?? item.fulfillmentOrderLineItemId ?? `${group.id}-${index}`}
                            item={item}
                        />
                    ))}
                </div>

                {tracking.length > 0 ? (
                    <>
                        <Separator className="my-4" />
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-foreground">{t('tracking.label')}</p>
                            {tracking.map((entry, index) => {
                                // Carrier URLs come from SOM/upstream, so run them through the repo's
                                // sanitizer: it blocks javascript:/data: and host-spoof tricks that
                                // rel="noopener" does not. When it isn't safe, show plain text (no link).
                                const safeUrl = ensureExternalUrl(entry.url);
                                return (
                                    <Fragment key={entry.number || `${group.id}-track-${index}`}>
                                        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                                            <span className="text-muted-foreground">{entry.number}</span>
                                            {safeUrl ? (
                                                <a
                                                    href={safeUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-primary hover:text-primary/80">
                                                    {t('tracking.trackShipment')}
                                                    <ExternalLink className="size-3.5 shrink-0" aria-hidden />
                                                </a>
                                            ) : null}
                                        </div>
                                    </Fragment>
                                );
                            })}
                        </div>
                    </>
                ) : null}
            </CardContent>
        </Card>
    );
}
